import { useCallback, useState } from "react";
import {
  AdminManagedUser,
  lookupAdminUser,
  updateAdminUserProducts,
} from "../../../services/platformApi";

export const useUserProductEditor = () => {
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [lookupUser, setLookupUser] = useState<AdminManagedUser | null>(null);
  const [editableProducts, setEditableProducts] = useState<string[]>([]);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateMessage, setUpdateMessage] = useState("");
  const [isLookupProductDropdownOpen, setLookupProductDropdownOpen] = useState(false);

  const toggleEditableProductSelection = useCallback((productKey: string) => {
    setEditableProducts((current) =>
      current.includes(productKey)
        ? current.filter((value) => value !== productKey)
        : [...current, productKey]
    );
  }, []);

  const handleLookupUser = useCallback(async () => {
    if (!lookupEmail.trim()) {
      setLookupError("Įveskite el. pašto adresą.");
      return;
    }

    setLookupLoading(true);
    setLookupError("");
    setUpdateMessage("");

    try {
      const user = await lookupAdminUser(lookupEmail);
      setLookupUser(user);
      setEditableProducts(user.products);
    } catch (error) {
      setLookupUser(null);
      setEditableProducts([]);
      setLookupError(error instanceof Error ? error.message : "Nepavyko rasti naudotojo");
    } finally {
      setLookupLoading(false);
    }
  }, [lookupEmail]);

  const handleUpdateProducts = useCallback(async () => {
    if (!lookupUser) {
      return;
    }

    setUpdateLoading(true);
    setLookupError("");
    setUpdateMessage("");

    try {
      const response = await updateAdminUserProducts(lookupUser.normalizedEmail, editableProducts);
      setLookupUser(response.user);
      setEditableProducts(response.user.products);

      const changeMessages = [
        response.addedProductTitles.length > 0
          ? `Pridėta: ${response.addedProductTitles.join(", ")}`
          : "",
        response.removedProductTitles.length > 0
          ? `Pašalinta: ${response.removedProductTitles.join(", ")}`
          : "",
      ].filter(Boolean);

      setUpdateMessage(
        changeMessages.length > 0
          ? changeMessages.join(" • ")
          : "Produktų sąrašas išsaugotas be papildomų pakeitimų."
      );
    } catch (error) {
      setLookupError(error instanceof Error ? error.message : "Nepavyko atnaujinti produktų");
    } finally {
      setUpdateLoading(false);
    }
  }, [editableProducts, lookupUser]);

  return {
    lookupEmail,
    setLookupEmail,
    lookupLoading,
    lookupError,
    lookupUser,
    editableProducts,
    updateLoading,
    updateMessage,
    isLookupProductDropdownOpen,
    setLookupProductDropdownOpen,
    toggleEditableProductSelection,
    handleLookupUser,
    handleUpdateProducts,
  };
};
